/* ============================================================
   PORTIONS — a video item plays one-or-more time ranges of its
   source video, in order, instead of the whole thing. Empty/absent
   portions ⇒ the whole video [0, durationSeconds].

   TWO timelines per item:
     • video-seconds  — position in the real YouTube video (what
       seekTo/getCurrentTime speak).
     • content-seconds — position along the KEPT content only (the
       sum of portion lengths). The scheduler, drift math, and the
       ad injector reason in content-seconds; we convert to
       video-seconds only at the player boundary (seek/swap).

   Powers SponsorBlock (kept = video minus sponsor ranges) and
   mid-content ads (split an item's portions around an ad). All pure.
   ============================================================ */

export interface Portion {
  start: number
  end: number
}

interface PortionedItem {
  durationSeconds?: number
  portions?: Portion[]
}

/** Minimum kept slice — sub-second portions are dropped/snapped (seek granularity is ~1s). */
export const MIN_PORTION_S = 1

/** The concrete ranges an item plays: its `portions` if present & non-empty, else the whole video
 *  `[{0, durationSeconds}]`. Returns `[]` when the duration is unknown (≤0) and no portions — the
 *  caller then treats playback as a plain whole video (identity, no conversion). */
export function effectivePortions(item: PortionedItem): Portion[] {
  if (item.portions && item.portions.length) return item.portions
  const d = item.durationSeconds ?? 0
  return d > 0 ? [{ start: 0, end: d }] : []
}

/** Total playable (content) length of an item, in seconds = Σ(end−start) of its portions. */
export function itemContentDuration(item: PortionedItem): number {
  const ps = effectivePortions(item)
  return ps.reduce((a, p) => a + Math.max(0, p.end - p.start), 0)
}

/** content-second → video-second within a concrete (non-empty) portion list. Clamps:
 *  ≤0 → first portion start; ≥total → last portion end. */
export function contentSecToVideoSec(portions: Portion[], contentSec: number): number {
  if (!portions.length) return contentSec // whole-video: pure identity (engine keeps raw behaviour)
  if (contentSec <= 0) return portions[0].start
  let acc = 0
  for (const p of portions) {
    const len = Math.max(0, p.end - p.start)
    if (contentSec < acc + len) return p.start + (contentSec - acc)
    acc += len
  }
  return portions[portions.length - 1].end
}

/** video-second → content-second. A video-second that falls inside a removed gap maps to the content
 *  boundary at the START of the next kept portion (so a player that overran into a gap reads as "at
 *  the boundary" and one corrective seek fixes it). Clamps to [0, total]. */
export function videoSecToContentSec(portions: Portion[], videoSec: number): number {
  if (!portions.length) return videoSec // whole-video: pure identity (engine keeps raw behaviour)
  let acc = 0
  for (const p of portions) {
    const len = Math.max(0, p.end - p.start)
    if (videoSec < p.start) return acc // in the gap before this portion → its content boundary
    if (videoSec <= p.end) return acc + (videoSec - p.start)
    acc += len
  }
  return acc // past the last portion → total content
}

/** Kept content of a video after removing `segments` (e.g. SponsorBlock sponsor ranges). Sorts +
 *  merges overlapping/adjacent segments, clamps to [0, durationSeconds], returns the complement.
 *  Sub-`MIN_PORTION_S` kept slices are dropped. Returns `[]` (= "whole video") when nothing is
 *  actually removed, so the blob stays minimal and the whole-video invariant holds. */
export function subtractSegments(durationSeconds: number, segments: Portion[]): Portion[] {
  if (durationSeconds <= 0 || !segments.length) return []
  const clamped = segments
    .map((s) => ({ start: Math.max(0, Math.min(durationSeconds, s.start)), end: Math.max(0, Math.min(durationSeconds, s.end)) }))
    .filter((s) => s.end > s.start)
    .sort((a, b) => a.start - b.start)
  if (!clamped.length) return []
  const merged: Portion[] = [clamped[0]]
  for (let i = 1; i < clamped.length; i++) {
    const last = merged[merged.length - 1]
    if (clamped[i].start <= last.end) last.end = Math.max(last.end, clamped[i].end)
    else merged.push(clamped[i])
  }
  const kept: Portion[] = []
  let cursor = 0
  for (const s of merged) {
    if (s.start - cursor >= MIN_PORTION_S) kept.push({ start: cursor, end: s.start })
    cursor = s.end
  }
  if (durationSeconds - cursor >= MIN_PORTION_S) kept.push({ start: cursor, end: durationSeconds })
  // no real removal (single full-span kept range) ⇒ treat as whole video.
  if (kept.length === 1 && kept[0].start === 0 && kept[0].end === durationSeconds) return []
  return kept
}

/** Decide what to do given the foreground's current VIDEO time within a multi-portion item:
 *  - `seek` — the player is in (or before) a removed gap; jump to the next kept portion's start.
 *  - `end` — the player is past the last kept portion; the item's content is over → advance.
 *  - `null` — inside a kept portion, nothing to do (single-portion items always return null; the
 *    real YouTube ENDED event is their backstop). `eps` absorbs getCurrentTime granularity. */
export type PortionStep =
  | { kind: 'seek'; videoSec: number; portionIndex: number }
  | { kind: 'end' }
  | null
export function portionAdvance(portions: Portion[], fgVideoSec: number, eps = 0.4): PortionStep {
  // NOTE: runs for items with EXPLICIT portions, INCLUDING a single one (a split half, or a
  // sponsor-trimmed video, ends BEFORE the source video's real end → must advance, not wait for ENDED).
  // Whole videos carry no portions ([]) and are handled by the YouTube ENDED event instead.
  if (!portions.length) return null
  for (let i = 0; i < portions.length; i++) {
    const p = portions[i]
    if (fgVideoSec < p.end - eps) {
      if (fgVideoSec < p.start - eps) return { kind: 'seek', videoSec: p.start, portionIndex: i }
      return null
    }
  }
  return { kind: 'end' }
}

/** Split a (concrete, non-empty) portion list at a CONTENT offset into [before, after]. The portion
 *  straddling the cut is divided in video-seconds. Used to insert a mid-content ad. */
export function splitPortionsAt(portions: Portion[], contentSec: number): [Portion[], Portion[]] {
  const total = portions.reduce((a, p) => a + Math.max(0, p.end - p.start), 0)
  if (contentSec <= 0) return [[], portions.slice()]
  if (contentSec >= total) return [portions.slice(), []]
  const before: Portion[] = []
  const after: Portion[] = []
  let acc = 0
  for (const p of portions) {
    const len = Math.max(0, p.end - p.start)
    if (acc + len <= contentSec) {
      before.push(p)
    } else if (acc >= contentSec) {
      after.push(p)
    } else {
      const cut = p.start + (contentSec - acc) // the video-second of the cut
      before.push({ start: p.start, end: cut })
      after.push({ start: cut, end: p.end })
    }
    acc += len
  }
  return [before, after]
}
